/**
 * API Service Layer
 * Centralized API client for all backend communication
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // Send httpOnly session cookies on every request (auth-session migration)
  withCredentials: true,
});

// Request interceptor for adding auth headers
apiClient.interceptors.request.use(
  (config) => {
    // Get stored auth data from sessionStorage (more secure than localStorage)
    const adminToken = sessionStorage.getItem('adminToken');
    const clientToken = sessionStorage.getItem('clientToken');
    const endUserToken = sessionStorage.getItem('endUserToken');
    
    // Add appropriate token based on the request
    if (adminToken && config.url?.includes('/admin')) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (clientToken) {
      config.headers.Authorization = `Bearer ${clientToken}`;
    } else if (endUserToken) {
      config.headers.Authorization = `Bearer ${endUserToken}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    
    if (response) {
      // Handle specific error codes
      switch (response.status) {
        case 401: {
          // Session expired/invalid — clear state and send user to the right login page
          console.warn('Unauthorized request - clearing session');
          const path = window.location.pathname;
          const isLoginPage =
            path === '/admin' || path === '/client' || path === '/login' || path === '/user-login';
          const isAuthCall = error.config?.url?.includes('/auth/');
          if (!isLoginPage && !isAuthCall) {
            sessionStorage.clear();
            if (path.startsWith('/admin')) {
              window.location.href = '/admin';
            } else if (path.startsWith('/client')) {
              window.location.href = '/client';
            } else if (path.startsWith('/dashboard')) {
              window.location.href = '/login';
            }
          }
          break;
        }
        case 403:
          console.warn('Forbidden - insufficient permissions');
          break;
        case 404:
          console.warn('Resource not found');
          break;
        case 500:
          console.error('Server error:', response.data?.detail || 'Unknown error');
          break;
        default:
          break;
      }
    } else if (error.request) {
      console.error('Network error - no response received');
    }
    
    return Promise.reject(error);
  }
);

// Helper to add client/user headers
const withClientHeaders = (clientId, userId = null) => {
  const headers = {};
  if (clientId) headers['X-Client-Id'] = clientId;
  if (userId) headers['X-User-Id'] = userId;
  return { headers };
};

// ==================== AUTH API ====================

export const authAPI = {
  // Admin login
  adminLogin: (usernameOrEmail, password) =>
    apiClient.post('/auth/login', { username: usernameOrEmail, password }),
  
  // Client (Sub-Admin) login
  clientLogin: (email, password) =>
    apiClient.post('/client/auth/login', { email, password }),
  
  // End-user login with email
  endUserLogin: (email, password) =>
    apiClient.post('/user/auth/login', { email, password }),
  
  // End-user login with username (for users created by clients)
  endUserLoginByUsername: (username, password) =>
    apiClient.post('/user/auth/login-username', { username, password }),
  
  // Get current user (rehydrates session from httpOnly cookie)
  getCurrentUser: () => apiClient.get('/auth/me'),

  // Clear httpOnly session cookie
  logout: () => apiClient.post('/auth/logout'),

  // Change password (admin self-service)
  changePassword: (currentPassword, newPassword) =>
    apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// ==================== ADMIN API ====================

export const adminAPI = {
  // Dashboard
  getDashboard: () => apiClient.get('/admin/dashboard'),
  
  // Clients
  getClients: () => apiClient.get('/admin/clients'),
  createClient: (data) => apiClient.post('/admin/clients', data),
  getClient: (id) => apiClient.get(`/admin/clients/${id}`),
  updateClient: (id, data) => apiClient.put(`/admin/clients/${id}`, data),
  deleteClient: (id) => apiClient.delete(`/admin/clients/${id}`),
  
  // Plans
  getPlans: () => apiClient.get('/admin/plans'),
  createPlan: (data) => apiClient.post('/admin/plans', data),
  updatePlan: (id, data) => apiClient.put(`/admin/plans/${id}`, data),
  deletePlan: (id) => apiClient.delete(`/admin/plans/${id}`),
  
  // Settings
  getSettings: () => apiClient.get('/admin/settings'),
  updateSettings: (data) => apiClient.put('/admin/settings', data),
};

// ==================== CLIENT API ====================

export const clientAPI = {
  // Dashboard
  getDashboard: (clientId) =>
    apiClient.get('/client/dashboard', withClientHeaders(clientId)),
  
  // End Users
  getUsers: (clientId, params = {}) =>
    apiClient.get('/client/users', {
      ...withClientHeaders(clientId),
      params
    }),
  createUser: (clientId, data) =>
    apiClient.post('/client/users', data, withClientHeaders(clientId)),
  getUser: (clientId, userId) =>
    apiClient.get(`/client/users/${userId}`, withClientHeaders(clientId)),
  updateUser: (clientId, userId, data) =>
    apiClient.put(`/client/users/${userId}`, data, withClientHeaders(clientId)),
  deleteUser: (clientId, userId) =>
    apiClient.delete(`/client/users/${userId}`, withClientHeaders(clientId)),
  getUserLimit: (clientId) =>
    apiClient.get('/client/user-limit', withClientHeaders(clientId)),
  
  // AI Agents
  getAgents: (clientId) =>
    apiClient.get('/client/agents', withClientHeaders(clientId)),
  createAgent: (clientId, data) =>
    apiClient.post('/client/agents', data, withClientHeaders(clientId)),
  getAgent: (clientId, agentId) =>
    apiClient.get(`/client/agents/${agentId}`, withClientHeaders(clientId)),
  updateAgent: (clientId, agentId, data) =>
    apiClient.put(`/client/agents/${agentId}`, data, withClientHeaders(clientId)),
  deleteAgent: (clientId, agentId) =>
    apiClient.delete(`/client/agents/${agentId}`, withClientHeaders(clientId)),
  updateAgentScript: (clientId, agentId, script) =>
    apiClient.put(`/client/agents/${agentId}/script`, { script }, withClientHeaders(clientId)),
  assignPhoneToAgent: (clientId, agentId, phoneNumber) =>
    apiClient.post(`/client/agents/${agentId}/assign-phone`, { phone_number: phoneNumber }, withClientHeaders(clientId)),
  unassignPhoneFromAgent: (clientId, agentId) =>
    apiClient.delete(`/client/agents/${agentId}/assign-phone`, withClientHeaders(clientId)),
  
  // Settings and Usage
  getSettings: (clientId) =>
    apiClient.get('/client/my-settings', withClientHeaders(clientId)),
  getUsage: (clientId) =>
    apiClient.get('/client/usage', withClientHeaders(clientId)),
  
  // Phone Numbers
  getPhoneNumbers: (clientId) =>
    apiClient.get('/client/phone-numbers', withClientHeaders(clientId)),
  purchasePhoneNumber: (clientId, data) =>
    apiClient.post('/client/phone-numbers/purchase', data, withClientHeaders(clientId)),
  searchPhoneNumbers: (clientId, params) =>
    apiClient.get('/client/phone-numbers/search', { 
      ...withClientHeaders(clientId),
      params 
    }),
  
  // Telnyx Configuration
  getTelnyxConfig: (clientId) =>
    apiClient.get(`/client/${clientId}/telnyx-config`, withClientHeaders(clientId)),
  saveTelnyxConfig: (clientId, data) =>
    apiClient.post(`/client/${clientId}/telnyx-config`, data, withClientHeaders(clientId)),
  updateTelnyxConfig: (clientId, data) =>
    apiClient.put(`/client/${clientId}/telnyx-config`, data, withClientHeaders(clientId)),
  deleteTelnyxConfig: (clientId) =>
    apiClient.delete(`/client/${clientId}/telnyx-config`, withClientHeaders(clientId)),
  verifyTelnyxConfig: (clientId) =>
    apiClient.post(`/client/${clientId}/telnyx-config/verify`, {}, withClientHeaders(clientId)),
  getTelnyxStatus: (clientId) =>
    apiClient.get(`/client/${clientId}/telnyx-config/status`, withClientHeaders(clientId)),
};

// ==================== VOICE AGENT API ====================
export const voiceAgentAPI = {
  // Test call to agent
  testCall: (agentId, phoneNumber) =>
    apiClient.post(`/client/agents/${agentId}/test-call`, { phone_number: phoneNumber }),
  
  // Validate outbound caller ID against Telnyx
  validateCallerId: (clientId, phoneNumber) =>
    apiClient.post('/client/agents/validate-caller-id', { phone_number: phoneNumber }, withClientHeaders(clientId)),
};

// ==================== CAMPAIGNS API ====================

export const campaignsAPI = {
  // List campaigns
  getAll: (clientId, params = {}) =>
    apiClient.get('/client/campaigns', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get single campaign
  get: (clientId, campaignId) =>
    apiClient.get(`/client/campaigns/${campaignId}`, withClientHeaders(clientId)),
  
  // Create campaign
  create: (clientId, data) =>
    apiClient.post('/client/campaigns', data, withClientHeaders(clientId)),
  
  // Update campaign
  update: (clientId, campaignId, data) =>
    apiClient.put(`/client/campaigns/${campaignId}`, data, withClientHeaders(clientId)),
  
  // Delete campaign
  delete: (clientId, campaignId) =>
    apiClient.delete(`/client/campaigns/${campaignId}`, withClientHeaders(clientId)),
  
  // Campaign control actions
  control: (clientId, campaignId, action) =>
    apiClient.post(`/client/campaigns/${campaignId}/${action}`, {}, withClientHeaders(clientId)),
  
  // Campaign actions (aliases)
  start: (clientId, campaignId) =>
    apiClient.post(`/client/campaigns/${campaignId}/start`, {}, withClientHeaders(clientId)),
  pause: (clientId, campaignId) =>
    apiClient.post(`/client/campaigns/${campaignId}/pause`, {}, withClientHeaders(clientId)),
  resume: (clientId, campaignId) =>
    apiClient.post(`/client/campaigns/${campaignId}/resume`, {}, withClientHeaders(clientId)),
  
  // Export campaign results
  export: (clientId, campaignId) =>
    apiClient.get(`/client/campaigns/${campaignId}/export`, withClientHeaders(clientId)),
  
  // Upload file for campaign
  uploadFile: (clientId, formData) =>
    apiClient.post('/client/campaigns/upload', formData, {
      ...withClientHeaders(clientId),
      headers: {
        ...withClientHeaders(clientId).headers,
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  // Extract numbers from uploaded file
  extractNumbers: (clientId, uploadId, data) =>
    apiClient.post('/client/campaigns/extract-numbers', data, {
      ...withClientHeaders(clientId),
      params: { upload_id: uploadId }
    }),
  
  // Upload contacts
  uploadContacts: (clientId, campaignId, formData) =>
    apiClient.post(`/client/campaigns/${campaignId}/contacts/upload`, formData, {
      ...withClientHeaders(clientId),
      headers: {
        ...withClientHeaders(clientId).headers,
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  // Calling hours
  getTimezones: () => apiClient.get('/client/campaigns/timezones'),
  updateCallingHours: (clientId, campaignId, data) =>
    apiClient.put(`/client/campaigns/${campaignId}/calling-hours`, data, withClientHeaders(clientId)),
  
  // Health check
  getHealth: (clientId) =>
    apiClient.get('/client/campaigns/health', withClientHeaders(clientId)),
  
  // Campaign Health endpoints
  getCampaignHealth: (clientId) =>
    apiClient.get('/client/campaign-health/health', withClientHeaders(clientId)),
  getResumeHistory: (clientId, days = 30) =>
    apiClient.get('/client/campaign-health/resume-history', {
      ...withClientHeaders(clientId),
      params: { days }
    }),
  getCampaignHealthDetail: (clientId, campaignId) =>
    apiClient.get(`/client/campaign-health/${campaignId}/health-detail`, withClientHeaders(clientId)),
};

// ==================== AGENT MONITOR API ====================

export const agentMonitorAPI = {
  // Get summary
  getSummary: (clientId) =>
    apiClient.get('/agent-monitor/summary', withClientHeaders(clientId)),
  
  // Get agents list
  getAgents: (clientId, statusFilter = null) =>
    apiClient.get('/agent-monitor/agents', {
      ...withClientHeaders(clientId),
      params: statusFilter ? { status_filter: statusFilter } : {}
    }),
  
  // Update status (called by agents)
  updateStatus: (userId, status, pauseReason = null) =>
    apiClient.put('/agent-monitor/status', 
      { status, pause_reason: pauseReason },
      { headers: { 'X-User-Id': userId } }
    ),
  
  // Agent login/logout
  agentLogin: (userId) =>
    apiClient.post('/agent-monitor/login', {}, { headers: { 'X-User-Id': userId } }),
  agentLogout: (userId) =>
    apiClient.post('/agent-monitor/logout', {}, { headers: { 'X-User-Id': userId } }),
  
  // Call events
  callStarted: (userId, callId, callNumber) =>
    apiClient.post(`/agent-monitor/call-started?call_id=${callId}&call_number=${encodeURIComponent(callNumber)}`, 
      {}, { headers: { 'X-User-Id': userId } }),
  callEnded: (userId, callDuration = 0) =>
    apiClient.post(`/agent-monitor/call-ended?call_duration=${callDuration}`, 
      {}, { headers: { 'X-User-Id': userId } }),
};

// ==================== LIVE CALLS API ====================

export const liveCallsAPI = {
  // Get all live calls
  getCalls: (clientId) =>
    apiClient.get('/live-calls', withClientHeaders(clientId)),
  
  // Get single call details
  getCall: (clientId, callId) =>
    apiClient.get(`/live-calls/${callId}`, withClientHeaders(clientId)),
  
  // Supervisor actions
  monitor: (clientId, userId, callId) =>
    apiClient.post(`/live-calls/${callId}/monitor`, {}, {
      headers: { 'X-Client-Id': clientId, 'X-User-Id': userId }
    }),
  
  whisper: (clientId, userId, callId, message) =>
    apiClient.post(`/live-calls/${callId}/whisper?message=${encodeURIComponent(message)}`, {}, {
      headers: { 'X-Client-Id': clientId, 'X-User-Id': userId }
    }),
  
  barge: (clientId, userId, callId) =>
    apiClient.post(`/live-calls/${callId}/barge`, {}, {
      headers: { 'X-Client-Id': clientId, 'X-User-Id': userId }
    }),
  
  takeover: (clientId, userId, callId) =>
    apiClient.post(`/live-calls/${callId}/takeover`, {}, {
      headers: { 'X-Client-Id': clientId, 'X-User-Id': userId }
    }),
  
  transfer: (clientId, userId, callId, targetUserId, transferType = 'blind') =>
    apiClient.post(`/live-calls/${callId}/transfer`, 
      { target_user_id: targetUserId, transfer_type: transferType },
      { headers: { 'X-Client-Id': clientId, 'X-User-Id': userId } }
    ),
  
  stopMonitoring: (clientId, userId, callId) =>
    apiClient.post(`/live-calls/${callId}/stop-monitoring`, {}, {
      headers: { 'X-Client-Id': clientId, 'X-User-Id': userId }
    }),
};

// ==================== CALL LOGS API ====================

export const callLogsAPI = {
  // Get call logs
  getLogs: (clientId, params = {}) =>
    apiClient.get('/client/call-logs', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get single call log
  getLog: (clientId, callId) =>
    apiClient.get(`/client/call-logs/${callId}`, withClientHeaders(clientId)),
  
  // Get call recording
  getRecording: (callId) =>
    apiClient.get(`/calls/${callId}/recording`, { responseType: 'blob' }),
  
  // Get call transcript
  getTranscript: (clientId, callId) =>
    apiClient.get(`/client/call-logs/${callId}/transcript`, withClientHeaders(clientId)),
  
  // Get call history for end user
  getHistory: (userId) =>
    apiClient.get('/calls/history', { headers: { 'X-User-Id': userId } }),
  
  // Make outbound call
  makeOutbound: (userId, toNumber, agentId) =>
    apiClient.post('/calls/outbound', 
      { to_number: toNumber, agent_id: agentId }, 
      { headers: { 'X-User-Id': userId } }
    ),
  
  // Control call (hangup, mute, unmute)
  control: (userId, callId, action) =>
    apiClient.post(`/calls/${callId}/control`, 
      { action }, 
      { headers: { 'X-User-Id': userId } }
    ),
  
  // Submit call details
  submitDetails: (formData, config) =>
    apiClient.post('/call-details', formData, config),
};

// ==================== ANALYTICS API ====================

export const analyticsAPI = {
  // Get overview stats
  getOverview: (clientId, params = {}) =>
    apiClient.get('/analytics/overview', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get call metrics
  getCallMetrics: (clientId, params = {}) =>
    apiClient.get('/analytics/calls', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get agent performance
  getAgentPerformance: (clientId, params = {}) =>
    apiClient.get('/analytics/agents', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get campaign performance
  getCampaignPerformance: (clientId, params = {}) =>
    apiClient.get('/analytics/campaigns', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get peak hours
  getPeakHours: (clientId, params = {}) =>
    apiClient.get('/analytics/peak-hours', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get active calls
  getActiveCalls: (clientId) =>
    apiClient.get('/analytics/calls/active', withClientHeaders(clientId)),
  
  // Get recent calls
  getRecentCalls: (clientId, params = {}) =>
    apiClient.get('/analytics/calls/recent', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get call outcomes
  getCallOutcomes: (clientId, params = {}) =>
    apiClient.get('/api/analytics/call-outcomes', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get calls with recordings
  getCallsWithRecordings: (clientId, params = {}) =>
    apiClient.get('/api/analytics/calls/with-recordings', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Search transcripts
  searchTranscripts: (clientId, params) =>
    apiClient.get('/analytics/transcripts/search', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Export transcripts
  exportTranscripts: (clientId, params) =>
    apiClient.get('/analytics/transcripts/export', {
      ...withClientHeaders(clientId),
      params,
      responseType: 'blob'
    }),
    
  // Voice analytics
  voiceAnalytics: {
    getTalkTime: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/talk-time-analysis', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
    getKeywords: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/keyword-analysis', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
    getOutcomes: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/outcome-analysis', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
    getDailyTrends: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/daily-trends', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
    // Advanced Analytics
    getConversationIntelligence: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/conversation-intelligence', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
    getPerformanceInsights: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/performance-insights', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
    getPeriodComparison: (clientId, currentDays = 7, compareDays = 7) =>
      apiClient.get('/analytics/voice/period-comparison', {
        ...withClientHeaders(clientId),
        params: { current_days: currentDays, compare_days: compareDays }
      }),
    getAgentComparison: (clientId, days = 30) =>
      apiClient.get('/analytics/voice/agent-comparison', {
        ...withClientHeaders(clientId),
        params: { days }
      }),
  }
};

// ==================== DIALER API ====================

export const dialerAPI = {
  // Get phone numbers
  getPhoneNumbers: (clientId) =>
    apiClient.get('/dialer/phone-numbers', withClientHeaders(clientId)),
  
  // Make outbound call
  makeCall: (userId, clientId, phoneNumber, agentId = null) =>
    apiClient.post('/dialer/call', 
      { to_number: phoneNumber, agent_id: agentId },
      { headers: { 'X-User-Id': userId, 'X-Client-Id': clientId } }
    ),
  
  // End call
  endCall: (userId, callId) =>
    apiClient.post(`/dialer/call/${callId}/end`, {}, 
      { headers: { 'X-User-Id': userId } }
    ),
  
  // Get WebRTC token
  getToken: (userId, clientId) =>
    apiClient.post('/dialer/webrtc-token', {},
      { headers: { 'X-User-Id': userId, 'X-Client-Id': clientId } }
    ),
  
  // Get call history
  getCallHistory: (clientId, params = {}) =>
    apiClient.get('/dialer/history', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get contacts
  getContacts: (clientId) =>
    apiClient.get('/dialer/contacts', withClientHeaders(clientId)),
  
  // Create contact
  createContact: (clientId, data) =>
    apiClient.post('/dialer/contacts', data, withClientHeaders(clientId)),
  
  // Update contact
  updateContact: (clientId, contactId, data) =>
    apiClient.put(`/dialer/contacts/${contactId}`, data, withClientHeaders(clientId)),
  
  // Delete contact
  deleteContact: (clientId, contactId) =>
    apiClient.delete(`/dialer/contacts/${contactId}`, withClientHeaders(clientId)),
  
  // Toggle favorite
  toggleFavorite: (clientId, contactId) =>
    apiClient.post(`/dialer/contacts/${contactId}/favorite`, {}, withClientHeaders(clientId)),
  
  // Get favorites
  getFavorites: (clientId) =>
    apiClient.get('/dialer/favorites', withClientHeaders(clientId)),
};

// ==================== DNC (Do Not Call) API ====================

export const dncAPI = {
  // Get DNC list
  getList: (clientId, params = {}) =>
    apiClient.get('/client/dnc', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Add to DNC
  addNumber: (clientId, phoneNumber, reason = null) =>
    apiClient.post('/client/dnc', 
      { phone_number: phoneNumber, reason },
      withClientHeaders(clientId)
    ),
  
  // Quick add (for end users)
  quickAdd: (userId, phoneNumber, reason, callId = null) =>
    apiClient.post('/users/dnc/quick-add', 
      { phone_number: phoneNumber, reason, call_id: callId },
      { headers: { 'X-User-Id': userId } }
    ),
  
  // Remove from DNC
  removeNumber: (clientId, id) =>
    apiClient.delete(`/client/dnc/${id}`, withClientHeaders(clientId)),
  
  // Check if number is on DNC
  checkNumber: (clientId, phoneNumber) =>
    apiClient.get(`/dnc/check/${encodeURIComponent(phoneNumber)}`, withClientHeaders(clientId)),
  
  // Scrub contacts against DNC list
  scrubContacts: (clientId, contacts) =>
    apiClient.post('/client/dnc/scrub', { contacts }, withClientHeaders(clientId)),
  
  // Bulk import
  bulkImport: (clientId, formData) =>
    apiClient.post('/client/dnc/import', formData, {
      ...withClientHeaders(clientId),
      headers: {
        ...withClientHeaders(clientId).headers,
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  // Export
  exportList: (clientId) =>
    apiClient.get('/client/dnc/export', {
      ...withClientHeaders(clientId),
      responseType: 'blob'
    }),
};

// ==================== USERS API (Admin) ====================

export const usersAPI = {
  // Get all users
  getAll: () => apiClient.get('/users'),
  
  // Create user
  create: (data) => apiClient.post('/users', data),
  
  // Update user
  update: (userId, data) => apiClient.put(`/users/${userId}`, data),
  
  // Delete user
  delete: (userId) => apiClient.delete(`/users/${userId}`),
  
  // Signup (public)
  signup: (data) => apiClient.post('/signup', data),
};

// ==================== SUB-ADMINS API (Admin) ====================

export const subAdminsAPI = {
  // Get all sub-admins
  getAll: () => apiClient.get('/sub-admins'),
  
  // Create sub-admin
  create: (data) => apiClient.post('/sub-admins', data),
  
  // Update sub-admin
  update: (id, data) => apiClient.put(`/sub-admins/${id}`, data),
  
  // Delete sub-admin
  delete: (id) => apiClient.delete(`/sub-admins/${id}`),
};

// ==================== PHONE NUMBERS API ====================

export const phoneNumbersAPI = {
  // Get all phone numbers
  getAll: () => apiClient.get('/phone-numbers'),
  
  // Search phone numbers
  search: (params) => apiClient.post('/phone-numbers/search', params),
  
  // Purchase phone number
  purchase: (phoneNumber) => apiClient.post('/phone-numbers/purchase', { phone_number: phoneNumber }),
  
  // Delete phone number
  delete: (phoneNumber) => apiClient.delete(`/phone-numbers/${encodeURIComponent(phoneNumber)}`),
  
  // Assign to agent
  assign: (phoneNumber, agentId) => 
    apiClient.post(`/phone-numbers/${encodeURIComponent(phoneNumber)}/assign`, { agent_id: agentId }),
  
  // Unassign from agent
  unassign: (phoneNumber) => 
    apiClient.post(`/phone-numbers/${encodeURIComponent(phoneNumber)}/unassign`),

  // One-click: assign to a client (existing agent or auto-create new agent)
  quickAssign: (phoneNumber, payload) =>
    apiClient.post(`/phone-numbers/${encodeURIComponent(phoneNumber)}/quick-assign`, payload),

  // Reconcile DB with Telnyx-owned numbers (self-heal after DB reset)
  sync: () => apiClient.post('/phone-numbers/sync'),
};

// ==================== PAYMENTS API ====================

export const paymentsAPI = {
  // Create checkout session
  createCheckout: (data) => apiClient.post('/payments/checkout', data),
  
  // Check payment status
  getStatus: (sessionId) => apiClient.get(`/payments/status/${sessionId}`),
};

// ==================== CONTACTS API (for Dialer Portal) ====================

export const contactsAPI = {
  // Get contacts
  getAll: (clientId) =>
    apiClient.get('/client/contacts', withClientHeaders(clientId)),
  
  // Create contact
  create: (clientId, data) =>
    apiClient.post('/client/contacts', data, withClientHeaders(clientId)),
  
  // Update contact
  update: (clientId, contactId, data) =>
    apiClient.patch(`/client/contacts/${contactId}`, data, withClientHeaders(clientId)),
  
  // Delete contact
  delete: (clientId, contactId) =>
    apiClient.delete(`/client/contacts/${contactId}`, withClientHeaders(clientId)),
};

// ==================== ADMIN RESOURCES API ====================

export const adminResourcesAPI = {
  // Get resource overview
  getOverview: () => apiClient.get('/admin/resource-overview'),
  
  // Get quota plans
  getQuotaPlans: () => apiClient.get('/admin/quota-plans'),
  
  // Apply plan to client
  applyPlan: (clientId, planName) =>
    apiClient.post(`/admin/clients/${clientId}/apply-plan?plan_name=${planName}`),
  
  // Get client quota
  getClientQuota: (clientId) =>
    apiClient.get(`/admin/clients/${clientId}/quota`),
  
  // Update client quota
  updateClientQuota: (clientId, quotaData) =>
    apiClient.put(`/admin/clients/${clientId}/quota`, quotaData),
};

// ==================== CLIENT SETTINGS API (Admin) ====================

export const clientSettingsAPI = {
  // Get client settings (admin)
  getSettings: (clientId) =>
    apiClient.get(`/admin/clients/${clientId}/settings`),
  
  // Get presets
  getPresets: () => apiClient.get('/admin/presets'),
  
  // Update client settings
  updateSettings: (clientId, settings) =>
    apiClient.put(`/admin/clients/${clientId}/settings`, settings),
  
  // Apply preset
  applyPreset: (clientId, presetName) =>
    apiClient.post(`/admin/clients/${clientId}/apply-preset`, { preset_name: presetName }),
  
  // Reset settings
  resetSettings: (clientId) =>
    apiClient.post(`/admin/clients/${clientId}/settings/reset`),
};

// ==================== AGENT VERSIONS API ====================

export const agentVersionsAPI = {
  // Get versions
  getVersions: (clientId, agentId) =>
    apiClient.get(`/client/agents/${agentId}/versions`, withClientHeaders(clientId)),
  
  // Restore version
  restoreVersion: (clientId, agentId, versionId) =>
    apiClient.post(`/client/agents/${agentId}/versions/${versionId}/restore`, {}, withClientHeaders(clientId)),
  
  // Delete version
  deleteVersion: (clientId, agentId, versionId) =>
    apiClient.delete(`/client/agents/${agentId}/versions/${versionId}`, withClientHeaders(clientId)),
};

// ==================== USAGE ALERTS API ====================

export const usageAlertsAPI = {
  // Check usage alerts
  check: (clientId) =>
    apiClient.get('/usage-alerts/check', withClientHeaders(clientId)),
  
  // Get alert history
  getHistory: (clientId) =>
    apiClient.get('/usage-alerts/history', withClientHeaders(clientId)),
  
  // Send test alert
  sendTest: (clientId) =>
    apiClient.post('/usage-alerts/test', {}, withClientHeaders(clientId)),
};

// ==================== REPORTS API ====================

export const reportsAPI = {
  // Get agents for reports
  getAgents: (clientId) =>
    apiClient.get('/client/agents', withClientHeaders(clientId)),
  
  // Get calls for reports
  getCalls: (clientId, params = {}) =>
    apiClient.get('/client/calls', { ...withClientHeaders(clientId), params }),
};

// ==================== RECENT CALLS API (for Agent Editor) ====================

export const recentCallsAPI = {
  // Analyze call with AI
  analyzeCall: (clientId, callId, analysisType) =>
    apiClient.post(`/client/calls/${callId}/analyze`, 
      { analysis_type: analysisType }, 
      withClientHeaders(clientId)
    ),
  
  // Get call recording URL
  getRecordingUrl: (clientId, callId) =>
    apiClient.get(`/client/calls/${callId}/recording-url`, withClientHeaders(clientId)),
  
  // Get call transcript
  getTranscript: (clientId, callId) =>
    apiClient.get(`/client/calls/${callId}/transcript`, withClientHeaders(clientId)),
  
  // Generate call summary
  generateSummary: (clientId, agentId, callId, forceRegenerate = false) =>
    apiClient.post(`/client/agents/${agentId}/calls/${callId}/summary`, 
      { force_regenerate: forceRegenerate }, 
      withClientHeaders(clientId)
    ),
  
  // Get agent calls
  getAgentCalls: (clientId, agentId, params = {}) =>
    apiClient.get(`/client/agents/${agentId}/calls`, {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get call transcription
  getCallTranscription: (clientId, agentId, callId, highlight = '') =>
    apiClient.get(`/client/agents/${agentId}/calls/${callId}/transcription${highlight ? `?highlight=${highlight}` : ''}`, 
      withClientHeaders(clientId)
    ),
};

// ==================== END USER API ====================

export const endUserAPI = {
  // Get profile
  getProfile: (userId) => 
    apiClient.get('/user/profile', { headers: { 'X-User-Id': userId } }),
  
  // Get agents
  getAgents: (userId) => 
    apiClient.get('/user/agents', { headers: { 'X-User-Id': userId } }),
  
  // Get call history
  getCallHistory: (userId) => 
    apiClient.get('/calls/history', { headers: { 'X-User-Id': userId } }),
  
  // Get SIP credentials
  getSipCredentials: (userId) =>
    apiClient.get('/user/sip-credentials', { headers: { 'X-User-Id': userId } }),
  
  // Get WebRTC token
  getWebRTCToken: (userId) =>
    apiClient.post('/dialer/webrtc-token', {}, { headers: { 'X-User-Id': userId } }),
  
  // Register WebRTC
  registerWebRTC: (userId, data) =>
    apiClient.post('/user/webrtc/register', data, { headers: { 'X-User-Id': userId } }),
  
  // Get availability
  getAvailability: (userId) =>
    apiClient.get(`/client/users/${userId}/availability`),
  
  // Set availability
  setAvailability: (userId, isAvailable) =>
    apiClient.put(`/client/users/${userId}/availability`, { is_available: isAvailable }),
  
  // Make outbound call
  makeCall: (userId, toNumber) =>
    apiClient.post('/calls/outbound', { to_number: toNumber }, { headers: { 'X-User-Id': userId } }),
  
  // Control call
  controlCall: (userId, callId, action, data = {}) =>
    apiClient.post(`/calls/${callId}/control`, { action, ...data }, { headers: { 'X-User-Id': userId } }),
};

// ==================== LEADS API ====================

export const leadsAPI = {
  // Get all leads with optional filters
  getLeads: (clientId, params = {}) =>
    apiClient.get('/client/leads', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get leads statistics
  getStats: (clientId) =>
    apiClient.get('/client/leads/stats', withClientHeaders(clientId)),
  
  // Get a single lead
  getLead: (clientId, leadId) =>
    apiClient.get(`/client/leads/${leadId}`, withClientHeaders(clientId)),
  
  // Create a new lead
  createLead: (clientId, leadData) =>
    apiClient.post('/client/leads', leadData, withClientHeaders(clientId)),
  
  // Update a lead
  updateLead: (clientId, leadId, updateData) =>
    apiClient.put(`/client/leads/${leadId}`, updateData, withClientHeaders(clientId)),
  
  // Delete a lead
  deleteLead: (clientId, leadId) =>
    apiClient.delete(`/client/leads/${leadId}`, withClientHeaders(clientId)),
  
  // Import leads in bulk
  importLeads: (clientId, leads) =>
    apiClient.post('/client/leads/import', { leads }, withClientHeaders(clientId)),
  
  // Bulk actions (delete, update_status, assign_agent)
  bulkAction: (clientId, leadIds, action, value = null) =>
    apiClient.post('/client/leads/bulk', { lead_ids: leadIds, action, value }, withClientHeaders(clientId)),
  
  // Record a call to a lead
  recordCall: (clientId, leadId) =>
    apiClient.post(`/client/leads/${leadId}/record-call`, {}, withClientHeaders(clientId)),
};


// ==================== FILES API ====================

export const filesAPI = {
  // Upload a file
  upload: (clientId, file, fileType = 'document') => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/client/files/upload?file_type=${fileType}`, formData, {
      ...withClientHeaders(clientId),
      headers: {
        ...withClientHeaders(clientId).headers,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // List all files
  getFiles: (clientId, params = {}) =>
    apiClient.get('/client/files', {
      ...withClientHeaders(clientId),
      params
    }),
  
  // Get file info
  getFile: (clientId, fileId) =>
    apiClient.get(`/client/files/${fileId}`, withClientHeaders(clientId)),
  
  // Download file
  downloadFile: (clientId, fileId) =>
    apiClient.get(`/client/files/${fileId}/download`, {
      ...withClientHeaders(clientId),
      responseType: 'blob'
    }),
  
  // Delete a file
  deleteFile: (clientId, fileId) =>
    apiClient.delete(`/client/files/${fileId}`, withClientHeaders(clientId)),
  
  // Bulk delete files
  bulkDelete: (clientId, fileIds) =>
    apiClient.post('/client/files/bulk-delete', fileIds, withClientHeaders(clientId)),
};


// ==================== VOICE CLONING API ====================

export const voiceCloningAPI = {
  // List all voices
  getVoices: (clientId) =>
    apiClient.get('/client/voices', withClientHeaders(clientId)),
  
  // Clone a voice from audio samples
  cloneVoice: (clientId, name, description, files) => {
    const formData = new FormData();
    formData.append('name', name);
    if (description) formData.append('description', description);
    files.forEach(file => formData.append('files', file));
    return apiClient.post('/client/voices/clone', formData, {
      ...withClientHeaders(clientId),
      headers: {
        ...withClientHeaders(clientId).headers,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Generate TTS audio
  generateTTS: (clientId, text, voiceId, stability = 0.5, similarityBoost = 0.75) =>
    apiClient.post('/client/voices/tts', {
      text,
      voice_id: voiceId,
      stability,
      similarity_boost: similarityBoost
    }, withClientHeaders(clientId)),
  
  // Delete a cloned voice
  deleteVoice: (clientId, voiceId) =>
    apiClient.delete(`/client/voices/${voiceId}`, withClientHeaders(clientId)),
  
  // Check if voice cloning is available
  getStatus: (clientId) =>
    apiClient.get('/client/voices/status', withClientHeaders(clientId)),
};


// ==================== SCRIPT ENHANCER API ====================

export const scriptEnhancerAPI = {
  // Enhance script with AI
  enhance: (data) =>
    apiClient.post('/script/enhance', data),
  
  // Get quick suggestions while typing
  quickSuggest: (data) =>
    apiClient.post('/script/quick-suggest', data),
  
  // Get template categories
  getTemplateCategories: () =>
    apiClient.get('/script/templates'),
  
  // Get specific template
  getTemplate: (category) =>
    apiClient.get(`/script/templates/${category}`),
};


// ==================== INTEGRATIONS API ====================

export const integrationsAPI = {
  // List available integrations
  getAvailable: () =>
    apiClient.get('/client/integrations/available'),
  
  // List connected integrations for a client
  getConnected: (clientId) =>
    apiClient.get('/client/integrations/connected', withClientHeaders(clientId)),
  
  // Connect an integration
  connect: (clientId, data) =>
    apiClient.post('/client/integrations/connect', data, withClientHeaders(clientId)),
  
  // Disconnect an integration
  disconnect: (clientId, integrationId) =>
    apiClient.delete(`/client/integrations/disconnect/${integrationId}`, withClientHeaders(clientId)),
  
  // Get integration status
  getStatus: (clientId, integrationId) =>
    apiClient.get(`/client/integrations/status/${integrationId}`, withClientHeaders(clientId)),
  
  // Cal.com specific
  calcom: {
    getEventTypes: (clientId) =>
      apiClient.get('/client/integrations/cal-com/event-types', withClientHeaders(clientId)),
    getAvailability: (clientId, eventTypeId, dateFrom, dateTo) =>
      apiClient.get('/client/integrations/cal-com/availability', {
        ...withClientHeaders(clientId),
        params: { event_type_id: eventTypeId, date_from: dateFrom, date_to: dateTo }
      }),
    bookMeeting: (clientId, data) =>
      apiClient.post('/client/integrations/cal-com/book', data, withClientHeaders(clientId)),
  },
  
  // Calendly specific
  calendly: {
    getEventTypes: (clientId) =>
      apiClient.get('/client/integrations/calendly/event-types', withClientHeaders(clientId)),
    getAvailability: (clientId, eventTypeUri, startTime, endTime) =>
      apiClient.get('/client/integrations/calendly/availability', {
        ...withClientHeaders(clientId),
        params: { event_type_uri: eventTypeUri, start_time: startTime, end_time: endTime }
      }),
    getScheduledEvents: (clientId, minStartTime, maxStartTime) =>
      apiClient.get('/client/integrations/calendly/scheduled-events', {
        ...withClientHeaders(clientId),
        params: { min_start_time: minStartTime, max_start_time: maxStartTime }
      }),
  },
  
  // Slack specific
  slack: {
    sendNotification: (clientId, message, channel) =>
      apiClient.post('/client/integrations/slack/send-notification', null, {
        ...withClientHeaders(clientId),
        params: { message, channel }
      }),
  },
  
  // WhatsApp specific
  whatsapp: {
    sendMessage: (clientId, data) =>
      apiClient.post('/client/integrations/whatsapp/send-message', data, withClientHeaders(clientId)),
    getTemplates: (clientId) =>
      apiClient.get('/client/integrations/whatsapp/templates', withClientHeaders(clientId)),
  },
  
  // Telegram specific
  telegram: {
    sendMessage: (clientId, data) =>
      apiClient.post('/client/integrations/telegram/send-message', data, withClientHeaders(clientId)),
    sendDocument: (clientId, chatId, documentUrl, caption) =>
      apiClient.post('/client/integrations/telegram/send-document', null, {
        ...withClientHeaders(clientId),
        params: { chat_id: chatId, document_url: documentUrl, caption }
      }),
    getBotInfo: (clientId) =>
      apiClient.get('/client/integrations/telegram/bot-info', withClientHeaders(clientId)),
    getNotificationSettings: (clientId) =>
      apiClient.get('/client/integrations/telegram/notification-settings', withClientHeaders(clientId)),
    updateNotificationSettings: (clientId, settings) =>
      apiClient.post('/client/integrations/telegram/notification-settings', settings, withClientHeaders(clientId)),
    testNotification: (clientId, chatId) =>
      apiClient.post(`/client/integrations/telegram/test-notification?chat_id=${chatId}`, null, withClientHeaders(clientId)),
  },
};


// ==================== UTILITY EXPORTS ====================

export { apiClient, API_BASE_URL, withClientHeaders };

// ==================== VOICE AI API ====================
// For TTS preview (blob) and chat (JSON) used by AgentHeader and DetailsTab

export const voiceAIAPI = {
  // Text-to-speech preview - returns audio blob
  preview: (provider, voiceId, text) =>
    apiClient.post('/voice/preview', {
      provider,
      voice_id: voiceId,
      text,
      _timestamp: Date.now(),
    }, {
      responseType: 'blob',
      timeout: 30000,
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' },
    }),

  // AI chat - returns JSON { success, response, session_id }
  chat: (message, sessionId, agentScript, agentName) =>
    apiClient.post('/voice/chat', {
      message,
      session_id: sessionId,
      agent_script: agentScript,
      agent_name: agentName,
    }, {
      timeout: 20000,
    }),
};

// ==================== CALL DETAILS API ====================
// Used by CallDetailsForm for draft management

export const callDetailsAPI = {
  // Get all drafts for a user
  getDrafts: (userId) =>
    apiClient.get('/call-details/drafts', { headers: { 'X-User-Id': userId } }),

  // Update a call detail (draft or captured)
  update: (detailId, data, userId) =>
    apiClient.put(`/call-details/${detailId}`, data, { headers: { 'X-User-Id': userId, 'Content-Type': 'application/json' } }),

  // Delete a call detail
  delete: (detailId, userId) =>
    apiClient.delete(`/call-details/${detailId}`, { headers: { 'X-User-Id': userId } }),
};

// ==================== CALL PLAYBACK API ====================
// Used by CallPlaybackModal

export const callPlaybackAPI = {
  // Get transcript details for a call
  getTranscript: (callId, clientId) =>
    apiClient.get(`/analytics/transcripts/${callId}`, withClientHeaders(clientId)),
};

export default {
  auth: authAPI,
  admin: adminAPI,
  client: clientAPI,
  campaigns: campaignsAPI,
  agentMonitor: agentMonitorAPI,
  liveCalls: liveCallsAPI,
  callLogs: callLogsAPI,
  analytics: analyticsAPI,
  dialer: dialerAPI,
  dnc: dncAPI,
  users: usersAPI,
  subAdmins: subAdminsAPI,
  phoneNumbers: phoneNumbersAPI,
  payments: paymentsAPI,
  endUser: endUserAPI,
  contacts: contactsAPI,
  adminResources: adminResourcesAPI,
  clientSettings: clientSettingsAPI,
  agentVersions: agentVersionsAPI,
  usageAlerts: usageAlertsAPI,
  reports: reportsAPI,
  recentCalls: recentCallsAPI,
  leads: leadsAPI,
  files: filesAPI,
  voiceCloning: voiceCloningAPI,
  scriptEnhancer: scriptEnhancerAPI,
  integrations: integrationsAPI,
  voiceAI: voiceAIAPI,
  callDetails: callDetailsAPI,
  callPlayback: callPlaybackAPI,
};
